import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AuthMarketingPanel, { HiroMark } from '@/components/auth/AuthMarketingPanel';
import BrandLoadingScreen from '@/components/shared/BrandLoadingScreen';
import { api } from '@/services/api';
import { PageSeo } from '@/components/seo/PageSeo';
import { NOINDEX_FOLLOW } from '@/lib/seo/constants';
import { loginSeo } from '@/lib/seo/authSeo';

type LoginView = 'login' | 'forgot' | 'forgot-sent';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [view, setView] = useState<LoginView>('login');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [passwordResetDone, setPasswordResetDone] = useState(false);
  const { login, loginWithGoogle, isLoading } = useAuth();
  const { t, language } = useLanguage();
  const isIt = language === 'it';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') ?? '/dashboard';

  useEffect(() => {
    if (searchParams.get('password-reset') === '1') {
      setPasswordResetDone(true);
      setView('login');
      const params = new URLSearchParams(searchParams);
      params.delete('password-reset');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordResetDone(false);
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError(t('login.invalidEmail'));
      return;
    }
    if (!password) {
      setError(t('login.error'));
      return;
    }
    try {
      await login(trimmedEmail, password);
      navigate(redirectTo, { replace: true });
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      const msg = (err as { message?: string })?.message ?? '';
      if (code === 'auth/invalid-email' || msg.includes('invalid-email')) {
        setError(t('login.invalidEmail'));
      } else if (msg.includes('account_disabled')) {
        setError(t('login.accountDisabled'));
      } else {
        setError(t('login.error'));
      }
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setForgotSubmitting(true);
    try {
      await api.auth.sendPasswordReset(email.trim());
      setView('forgot-sent');
    } catch {
      setError(t('login.forgotError'));
    } finally {
      setForgotSubmitting(false);
    }
  };

  const openForgot = () => {
    setError('');
    setPasswordResetDone(false);
    setView('forgot');
  };

  const backToLogin = () => {
    setError('');
    setView('login');
  };

  const signupLink =
    redirectTo !== '/dashboard'
      ? `/signup?redirect=${encodeURIComponent(redirectTo)}`
      : '/signup';

  const seo = loginSeo(language);

  // Full-screen loader during initial session check, email login, and Google
  // popup return + POST /auth/session (route stays mounted — see App.tsx).
  if (isLoading) {
    return (
      <>
        <PageSeo title={seo.title} path="/login" robots={NOINDEX_FOLLOW} />
        <BrandLoadingScreen />
      </>
    );
  }

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden grid grid-cols-1 lg:grid-cols-2 bg-brand-surface-light">
      <PageSeo title={seo.title} path="/login" robots={NOINDEX_FOLLOW} />
      <AuthMarketingPanel />

      <div className="lg:h-screen lg:overflow-y-auto">
        <div className="flex min-h-full flex-col items-center justify-center px-5 sm:px-8 py-10 lg:py-12">
        <div className="w-full max-w-sm">
          <div className="flex lg:hidden items-center justify-center gap-2 mb-8">
            <HiroMark className="h-9 w-9" />
            <span className="font-display font-bold text-xl tracking-tight">HiroMatch</span>
          </div>

          {view === 'login' && (
            <>
              <div className="mb-8">
                <h1 className="font-display font-bold text-3xl tracking-tight text-foreground">
                  {isIt ? 'Bentornato su HiroMatch' : 'Welcome back to HiroMatch'}
                </h1>
                <p className="text-sm text-muted-foreground mt-2">
                  {isIt
                    ? 'Accedi per gestire le tue posizioni e i tuoi candidati.'
                    : 'Sign in to manage your positions and candidates.'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                {passwordResetDone && (
                  <p className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-800/40 rounded-md px-3 py-2">
                    {t('login.passwordResetDone')}
                  </p>
                )}
                {error && <p className="text-sm text-destructive">{error}</p>}
                <div className="space-y-2">
                  <Label htmlFor="email">{t('login.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t('login.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>
                <Button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary-mid text-white" disabled={isLoading}>
                  {isLoading ? t('login.submitting') : t('login.submit')}
                </Button>

                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center">
                    <span className="bg-brand-surface-light px-2 text-xs text-muted-foreground uppercase tracking-wide">
                      {t('login.or')}
                    </span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  disabled={isLoading}
                  onClick={async () => {
                    setError('');
                    setPasswordResetDone(false);
                    try {
                      await loginWithGoogle();
                      navigate(redirectTo, { replace: true });
                    } catch (err: any) {
                      if (err?.code === 'NEW_GOOGLE_USER') {
                        const onboardingUrl =
                          redirectTo !== '/dashboard'
                            ? `/onboarding?fromSignup=true&skipCompany=true&redirect=${encodeURIComponent(redirectTo)}`
                            : '/onboarding?fromSignup=true';
                        navigate(onboardingUrl, { replace: true });
                        return;
                      }
                      const gMsg: string = err?.message ?? '';
                      setError(gMsg.includes('account_disabled') ? t('login.accountDisabled') : t('login.googleFailed'));
                    }
                  }}
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  {t('login.google')}
                </Button>

                <div className="text-center text-sm text-muted-foreground pt-1">
                  <button
                    type="button"
                    className="hover:text-foreground transition-colors"
                    onClick={openForgot}
                  >
                    {t('login.forgot')}
                  </button>
                </div>
              </form>

              <p className="text-center text-sm text-muted-foreground mt-8">
                {isIt ? "Non hai un account? " : "Don't have an account? "}
                <Link to={signupLink} className="text-brand-accent hover:text-brand-primary font-medium">
                  {isIt ? 'Inizia ora →' : 'Start now →'}
                </Link>
              </p>
            </>
          )}

          {view === 'forgot' && (
            <>
              <button
                type="button"
                onClick={backToLogin}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('login.backToLogin')}
              </button>

              <div className="mb-8">
                <h1 className="font-display font-bold text-3xl tracking-tight text-foreground">
                  {t('login.forgotTitle')}
                </h1>
                <p className="text-sm text-muted-foreground mt-2">
                  {t('login.forgotSubtitle')}
                </p>
              </div>

              <form onSubmit={handleForgotSubmit} className="space-y-4">
                {error && <p className="text-sm text-destructive">{error}</p>}
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">{t('login.email')}</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-brand-primary hover:bg-brand-primary-mid text-white"
                  disabled={forgotSubmitting}
                >
                  {forgotSubmitting ? t('login.forgotSubmitting') : t('login.forgotSubmit')}
                </Button>
              </form>
            </>
          )}

          {view === 'forgot-sent' && (
            <>
              <button
                type="button"
                onClick={backToLogin}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('login.backToLogin')}
              </button>

              <div className="mb-6">
                <h1 className="font-display font-bold text-3xl tracking-tight text-foreground">
                  {t('login.forgotSuccessTitle')}
                </h1>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                  {t('login.forgotSuccessBody')}
                </p>
              </div>

              <Button type="button" variant="outline" className="w-full" onClick={backToLogin}>
                {t('login.backToLogin')}
              </Button>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
